import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  CashSpecialIcon,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useComposeStore } from "../store/useComposeStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { listOperations } from "../services/api";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { landingChoice } from "../utils/landingChoice";

type OperationProbe = "loading" | "ready" | "error";

/**
 * Send intro screen reached after the magic link is claimed (and consent is
 * accepted). On arrival it checks existing operations: a first visit keeps the
 * start CTA (compose when allowed, otherwise the hub). A returning sender
 * gets a welcome-back title plus history and compose actions.
 */
export const LandingPage = memo(() => {
  const { t } = useTranslation();
  const { theme, setAmountState } = useStore();
  const { session } = useSessionStore();
  const { resetCompose } = useComposeStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const [busy, setBusy] = useState(false);
  const [operationCount, setOperationCount] = useState(0);
  const [probe, setProbe] = useState<OperationProbe>("loading");

  useEffect(() => {
    safeCapture(getEvents().LANDING_VIEW);
  }, []);

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;

  const linkId = linkIdFromRoute || session.data?.linkId;
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;
  const choice = landingChoice(operationCount, Boolean(linkId) && canSend);
  const returning = probe === "ready" && choice.visit === "returning";

  useEffect(() => {
    if (!linkId) {
      setOperationCount(0);
      setProbe("ready");
      return;
    }

    let cancelled = false;
    setProbe("loading");
    listOperations(linkId, { page: 1, limit: 1 })
      .then(({ items }) => {
        if (cancelled) return;
        setOperationCount(items.length);
        setProbe("ready");
      })
      .catch((error) => {
        console.error("[LandingPage] Failed to probe operations:", error);
        if (cancelled) return;
        setOperationCount(0);
        setProbe("error");
      });

    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const openHistory = useCallback(() => {
    redirectTo(APP_ROUTES.HOME_PAGE);
  }, [redirectTo]);

  const openCompose = useCallback(() => {
    resetCompose();
    setAmountState({ amount: "", anchor: "send" });
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  }, [resetCompose, setAmountState, redirectTo]);

  const handleGetStarted = useCallback(async () => {
    if (busy) return;

    if (probe === "ready") {
      if (choice.canCompose) openCompose();
      else openHistory();
      return;
    }

    if (!linkId) {
      openHistory();
      return;
    }

    setBusy(true);
    try {
      const { items } = await listOperations(linkId, { page: 1, limit: 1 });
      if (items.length === 0 && canSend) {
        openCompose();
        return;
      }
      openHistory();
    } catch (error) {
      console.error("[LandingPage] Failed to probe operations:", error);
      openHistory();
    } finally {
      setBusy(false);
    }
  }, [busy, probe, choice.canCompose, linkId, canSend, openCompose, openHistory]);

  const loading = probe === "loading";

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content
        className={loading ? undefined : "landing-section flow-hero-section"}
      >
        {loading ? (
          <div className="landing-section content-center loader-page">
            <section className="content">
              <div
                className="loader-spinner"
                role="status"
                aria-label={t("transfer.loadingQuote")}
              />
            </section>
          </div>
        ) : (
          <>
            <ScrollFadeIndicator color={colorConfig.bodyCopy} />
            <section className="flow-hero">
              <FlowHeroMark
                dropzoneColors={dropzoneColors}
                backgroundIcon={backgroundIcon}
              >
                <CashSpecialIcon size={60} colors={dropzoneColors} />
              </FlowHeroMark>
              <section className="landing-container landing-container--tight">
                <div className={returning ? "landing-returning-title" : undefined}>
                  <PageTitle
                    highlight={
                      returning
                        ? t("landing.returningGreeting")
                        : t("landing.greeting")
                    }
                    highlightColor={TITLE_COLOR}
                    secudnary={t("landing.subtitle")}
                    secudnaryColor={TITLE_COLOR}
                  />
                </div>
                <BodyCopy textColor={bodyCopy}>{t("landing.body")}</BodyCopy>
              </section>
            </section>
          </>
        )}
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {loading ? null : returning ? (
            <>
              {!choice.canCompose && (
                <BodyCopy
                  textColor={bodyCopy}
                  className="transactions-disabled-note"
                >
                  {t("transactions.sendsDisabled")}
                </BodyCopy>
              )}
              <section className="fund-request-actions fund-request-actions--stack">
                <Button
                  type="button"
                  size="large"
                  variant="outline"
                  background={backgroundBtn}
                  color={TITLE_COLOR}
                  style={{ backgroundColor: background }}
                  onClick={openHistory}
                >
                  {t("transactions.title")}
                </Button>
                <Button
                  size="large"
                  background={backgroundBtn}
                  color={textColorBtn}
                  disabled={!choice.canCompose}
                  onClick={openCompose}
                >
                  {t("transactions.newSend")}
                </Button>
              </section>
            </>
          ) : (
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={busy}
              onClick={() => void handleGetStarted()}
            >
              {t("landing.cta")}
            </Button>
          )}
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
